// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Two routes, because a crew is two screens: the list of what exists and the
// one you are looking at. There is no third — a team has no history, no
// documents and no money, and inventing tabs for a thing with six fields is how
// a screen stops being read.

export const routes = [
  {
    path: '/teams',
    name: 'teams',
    component: () => import('./pages/TeamsPage.vue'),
    meta: { permission: 'teams:read', title: 'teams.title' },
  },
  // Static before the parameter, for the reader: route ranking already puts it
  // first whatever the order here.
  {
    path: '/teams/new',
    name: 'team-new',
    component: () => import('./pages/TeamPage.vue'),
    meta: { permission: 'teams:manage', title: 'teams.newTitle' },
  },
  {
    path: '/teams/:id',
    name: 'team',
    component: () => import('./pages/TeamPage.vue'),
    meta: { permission: 'teams:read', title: 'teams.detailTitle' },
  },
]

/**
 * One entry. `teams:read` goes to everybody at install, so the crew see it too
 * — which is the point: for somebody on a van, «con quién voy» is the question,
 * and it should not need an office login to answer.
 */
export const navigation = [
  { to: '/teams', labelKey: 'teams.title', permission: 'teams:read', icon: 'crew', group: 'business' },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (§16): a module that ships two is a module that cannot be sold
// to the third client.
export const messages = {
  es: {
    teams: {
      title: 'Equipos',
      dashboard: {
        active: 'Equipos activos',
        activeHelp: 'Cuántos equipos están activos y se pueden asignar a un trabajo.',
        total: 'Equipos',
        totalHelp: 'Cuántos equipos hay dados de alta, activos e inactivos.',
        list: 'Los equipos',
        listHelp: 'Los equipos activos, con quién responde por cada uno y cuánta gente lo forma. Estira la tarjeta y caben más.',
      },
      subtitle: 'Quién va con quién, quién manda y de qué color se lee en el tablero',

      // El título y el resumen del tema de ayuda. Van aquí y no dentro del
      // `help/es.md` porque el índice de la documentación los necesita antes de
      // abrir nada, y cargar veinticinco módulos por cuatro idiomas para
      // dibujar una lista serían cientos de kilobytes en el arranque.
      help: {
        title: 'Equipos',
        subtitle: 'Cómo se forma una cuadrilla, por qué el responsable sale de dentro, y la diferencia entre inactivo y archivado',
      },

      detailTitle: 'Equipo',
      detailHint: 'El equipo: quién lo forma y quién responde por él',
      newTitle: 'Equipo nuevo',
      newHint: 'Un nombre, un color y la gente que va junta',
      mine: 'Mis equipos',
      mineHint: 'Los equipos en los que estás',
      empty: 'Todavía no hay ningún equipo',
      noLead: 'Sin responsable',
      noMembers: 'Todavía no hay nadie',
      noMembersHint: 'Añade a la gente que va junta. El responsable se elige entre ellos.',
      memberCount: '{count} persona | {count} personas',
      addMember: 'Añadir a alguien',
      addMemberPlaceholder: 'Elige a una persona…',
      removeMember: 'Quitar del equipo',
      makeLead: 'Marcar como responsable',
      saved: 'Equipo guardado',
      archived: 'Equipo archivado',
      restored: 'Equipo devuelto. Vuelve inactivo: enciéndelo cuando quieras planificar con él.',
      archive: { isArchived: 'Este equipo está archivado: no sale al planificar ni en el cuadrante, y no se puede editar. Devuélvelo para volver a usarlo.' },
      actions: { new: 'Nuevo equipo', archive: 'Archivar', restore: 'Devolver' },
      sections: {
        identity: 'El equipo',
        crew: 'El equipo',
        crewHint: 'El responsable tiene que ser uno de los que están dentro',
      },
      fields: {
        name: 'Nombre',
        colour: 'Color',
        description: 'Descripción',
        lead: 'Responsable',
        members: 'Quién va',
        state: 'Estado',
        stateHelp: 'Un equipo inactivo sigue existiendo, pero no se ofrece al planificar',
      },
      states: { active: 'Activo', inactive: 'Inactivo' },
      stats: { total: 'Equipos', active: 'Activos', inactive: 'Inactivos' },
      filters: {
        search: 'Buscar',
        searchPlaceholder: 'Nombre o descripción…',
        state: 'Estado',
        allStates: 'Todos',
      },
      confirmArchive: {
        title: '¿Archivar el equipo?',
        message: '«{name}» dejará de ofrecerse al planificar. El trabajo que ya lo nombra lo sigue nombrando.',
      },
      search: { subtitle: 'Equipo' },
    },
    modules: { teams: 'Equipos' },
    permissions: {
      teams: {
        read: 'Ver los equipos y saber con quién va cada uno',
        manage: 'Crear equipos y mover personas entre ellos',
      },
    },
    audit: {
      actions: {
        team: {
          created: 'Equipo creado',
          updated: 'Equipo modificado',
          member_added: 'Alguien entra en un equipo',
          member_removed: 'Alguien sale de un equipo',
          archived: 'Equipo archivado',
          restored: 'Equipo devuelto',
        },
      },
    },
    assistant: {
      openers: {
        teams: {
          'who-is-with-me': '¿Quién va hoy conmigo?',
          'what-teams': '¿Qué equipos tenemos?',
        },
      },
    },
    settings: {
      ns: {
        teams: {
          title: 'Equipos',
          subtitle: 'Cómo de estricta es esta empresa con sus equipos',
          requireLead: {
            label: 'Exigir un responsable en cada equipo',
            help: 'Desactivado de serie: una pareja que responde directamente a oficina no tiene responsable, y no dejar guardarla sería inventarse una jerarquía.',
          },
          oneTeamPerPerson: {
            label: 'Una persona solo puede estar en un equipo',
            help: 'Actívalo cuando los equipos son fijos y «dónde está Juan hoy» tiene que tener una sola respuesta.',
          },
        },
      },
    },
  },

  ca: {
    teams: {
      title: 'Equips',
      dashboard: {
        active: 'Equips actius',
        activeHelp: 'Quantes colles estan actives i es poden assignar a una feina.',
        total: 'Equips',
        totalHelp: 'Quants equips hi ha donats d\'alta, actius i inactius.',
        list: 'Els equips',
        listHelp: 'Els equips actius, amb qui respon per cadascun i quanta gent el forma. Estira la targeta i n\'hi caben més.',
      },
      subtitle: 'Qui va amb qui, qui mana i de quin color es llegeix al tauler',

      help: {
        title: 'Equips',
        subtitle: 'Com es forma una colla, per què el responsable surt de dins, i la diferència entre inactiu i arxivat',
      },

      detailTitle: 'Equip',
      detailHint: 'La colla: qui la forma i qui hi respon',
      newTitle: 'Equip nou',
      newHint: 'Un nom, un color i la gent que va junta',
      mine: 'Els meus equips',
      mineHint: 'Les colles on ets',
      empty: 'Encara no hi ha cap equip',
      noLead: 'Sense responsable',
      noMembers: 'Encara no hi ha ningú',
      noMembersHint: 'Afegeix la gent que va junta. El responsable es tria entre ells.',
      memberCount: '{count} persona | {count} persones',
      addMember: 'Afegir algú',
      addMemberPlaceholder: 'Tria una persona…',
      removeMember: 'Treure de l\'equip',
      makeLead: 'Marcar com a responsable',
      saved: 'Equip desat',
      archived: 'Equip arxivat',
      restored: 'Equip recuperat. Torna inactiu: encén-lo quan hi vulguis planificar.',
      archive: { isArchived: 'Aquest equip està arxivat: no surt en planificar ni al quadrant, i no es pot editar. Recupera’l per tornar-lo a fer servir.' },
      actions: { new: 'Equip nou', archive: 'Arxivar', restore: 'Recuperar' },
      sections: {
        identity: 'L\'equip',
        crew: 'La colla',
        crewHint: 'El responsable ha de ser un dels que hi són a dins',
      },
      fields: {
        name: 'Nom',
        colour: 'Color',
        description: 'Descripció',
        lead: 'Responsable',
        members: 'Qui hi va',
        state: 'Estat',
        stateHelp: 'Un equip inactiu segueix existint, però no s\'ofereix en planificar',
      },
      states: { active: 'Actiu', inactive: 'Inactiu' },
      stats: { total: 'Equips', active: 'Actius', inactive: 'Inactius' },
      filters: {
        search: 'Cercar',
        searchPlaceholder: 'Nom o descripció…',
        state: 'Estat',
        allStates: 'Tots',
      },
      confirmArchive: {
        title: 'Arxivar l\'equip?',
        message: '«{name}» deixarà d\'oferir-se en planificar. La feina que ja el nomena el segueix nomenant.',
      },
      search: { subtitle: 'Equip' },
    },
    modules: { teams: 'Equips' },
    permissions: {
      teams: {
        read: 'Veure els equips i saber amb qui va cadascú',
        manage: 'Crear equips i moure persones entre ells',
      },
    },
    audit: {
      actions: {
        team: {
          created: 'Equip creat',
          updated: 'Equip modificat',
          member_added: 'Algú entra en un equip',
          member_removed: 'Algú surt d\'un equip',
          archived: 'Equip arxivat',
          restored: 'Equip recuperat',
        },
      },
    },
    assistant: {
      openers: {
        teams: {
          'who-is-with-me': 'Qui va avui amb mi?',
          'what-teams': 'Quins equips tenim?',
        },
      },
    },
    settings: {
      ns: {
        teams: {
          title: 'Equips',
          subtitle: 'Com d\'estricta és aquesta empresa amb les seves colles',
          requireLead: {
            label: 'Exigir un responsable a cada equip',
            help: 'Desactivat de sèrie: una parella que respon directament a oficina no en té, i no deixar-la desar seria inventar-se una jerarquia.',
          },
          oneTeamPerPerson: {
            label: 'Una persona només pot ser en un equip',
            help: 'Activa\'l quan les colles són fixes i «on és el Joan avui» ha de tenir una sola resposta.',
          },
        },
      },
    },
  },

  en: {
    teams: {
      title: 'Teams',
      dashboard: {
        active: 'Active teams',
        activeHelp: 'How many crews are active and can be put on a job.',
        total: 'Teams',
        totalHelp: 'How many teams are on file, active and inactive alike.',
        list: 'The teams',
        listHelp: 'The active teams, with who answers for each and how many people are on it. Stretch the card and more fit.',
      },
      subtitle: 'Who works with whom, who leads them, and the colour they are read by on a board',

      help: {
        title: 'Teams',
        subtitle: 'How a crew is put together, why the lead comes from inside it, and the difference between inactive and archived',
      },

      detailTitle: 'Team',
      detailHint: 'The crew: who is on it and who answers for it',
      newTitle: 'New team',
      newHint: 'A name, a colour and the people who go together',
      mine: 'My teams',
      mineHint: 'The crews you are on',
      empty: 'No teams yet',
      noLead: 'No lead',
      noMembers: 'Nobody yet',
      noMembersHint: 'Add the people who go together. The lead is picked from among them.',
      memberCount: '{count} person | {count} people',
      addMember: 'Add somebody',
      addMemberPlaceholder: 'Pick a person…',
      removeMember: 'Take off the team',
      makeLead: 'Make lead',
      saved: 'Team saved',
      archived: 'Team archived',
      restored: 'Team brought back. It returns inactive: switch it on when you want to plan with it.',
      archive: { isArchived: 'This team is archived: it is off the planner and off the rota, and it cannot be edited. Bring it back to use it again.' },
      actions: { new: 'New team', archive: 'Archive', restore: 'Bring it back' },
      sections: {
        identity: 'The team',
        crew: 'The crew',
        crewHint: 'The lead has to be one of the people on it',
      },
      fields: {
        name: 'Name',
        colour: 'Colour',
        description: 'Description',
        lead: 'Lead',
        members: 'Who goes',
        state: 'State',
        stateHelp: 'An inactive team still exists, but is not offered when planning',
      },
      states: { active: 'Active', inactive: 'Inactive' },
      stats: { total: 'Teams', active: 'Active', inactive: 'Inactive' },
      filters: {
        search: 'Search',
        searchPlaceholder: 'Name or description…',
        state: 'State',
        allStates: 'All',
      },
      confirmArchive: {
        title: 'Archive the team?',
        message: '“{name}” stops being offered when planning. Work that already names it keeps naming it.',
      },
      search: { subtitle: 'Team' },
    },
    modules: { teams: 'Teams' },
    permissions: {
      teams: {
        read: 'See the teams and who is on each one',
        manage: 'Create teams and move people between them',
      },
    },
    audit: {
      actions: {
        team: {
          created: 'Team created',
          updated: 'Team changed',
          member_added: 'Somebody joined a team',
          member_removed: 'Somebody left a team',
          archived: 'Team archived',
          restored: 'Team brought back',
        },
      },
    },
    assistant: {
      openers: {
        teams: {
          'who-is-with-me': 'Who am I going with today?',
          'what-teams': 'What crews have we got?',
        },
      },
    },
    settings: {
      ns: {
        teams: {
          title: 'Teams',
          subtitle: 'How strict this company is about its own crews',
          requireLead: {
            label: 'Require a lead on every team',
            help: 'Off by default: a two-person crew answering to the office directly has none, and refusing to save it would be inventing a hierarchy.',
          },
          oneTeamPerPerson: {
            label: 'A person can only be on one team',
            help: 'Turn it on when the crews are fixed and “where is Juan today” must have one answer.',
          },
        },
      },
    },
  },

  fr: {
    teams: {
      title: 'Équipes',
      dashboard: {
        active: 'Équipes actives',
        activeHelp: 'Combien d\'équipes sont actives et peuvent être affectées à un travail.',
        total: 'Équipes',
        totalHelp: 'Combien d\'équipes sont enregistrées, actives et inactives confondues.',
        list: 'Les équipes',
        listHelp: 'Les équipes actives, avec qui en répond et combien de personnes la composent. Étirez la carte et il en tient plus.',
      },
      subtitle: 'Qui travaille avec qui, qui les dirige et la couleur qui les identifie sur un tableau',

      help: {
        title: 'Équipes',
        subtitle: 'Comment se forme une équipe, pourquoi le responsable vient de l\'intérieur, et la différence entre inactive et archivée',
      },

      detailTitle: 'Équipe',
      detailHint: 'L\'équipe : qui la compose et qui en répond',
      newTitle: 'Nouvelle équipe',
      newHint: 'Un nom, une couleur et les personnes qui vont ensemble',
      mine: 'Mes équipes',
      mineHint: 'Les équipes dont vous faites partie',
      empty: 'Aucune équipe pour le moment',
      noLead: 'Sans responsable',
      noMembers: 'Personne pour le moment',
      noMembersHint: 'Ajoutez les personnes qui vont ensemble. Le responsable se choisit parmi elles.',
      memberCount: '{count} personne | {count} personnes',
      addMember: 'Ajouter quelqu\'un',
      addMemberPlaceholder: 'Choisissez une personne…',
      removeMember: 'Retirer de l\'équipe',
      makeLead: 'Désigner responsable',
      saved: 'Équipe enregistrée',
      archived: 'Équipe archivée',
      restored: 'Équipe restaurée. Elle revient inactive : activez-la quand vous voudrez planifier avec elle.',
      archive: { isArchived: 'Cette équipe est archivée : elle n’apparaît ni dans le planning ni dans le tableau, et ne peut pas être modifiée. Restaurez-la pour la réutiliser.' },
      actions: { new: 'Nouvelle équipe', archive: 'Archiver', restore: 'Restaurer' },
      sections: {
        identity: 'L\'équipe',
        crew: 'La composition',
        crewHint: 'Le responsable doit faire partie de l\'équipe',
      },
      fields: {
        name: 'Nom',
        colour: 'Couleur',
        description: 'Description',
        lead: 'Responsable',
        members: 'Qui y va',
        state: 'État',
        stateHelp: 'Une équipe inactive existe toujours, mais n\'est pas proposée à la planification',
      },
      states: { active: 'Active', inactive: 'Inactive' },
      stats: { total: 'Équipes', active: 'Actives', inactive: 'Inactives' },
      filters: {
        search: 'Rechercher',
        searchPlaceholder: 'Nom ou description…',
        state: 'État',
        allStates: 'Toutes',
      },
      confirmArchive: {
        title: 'Archiver l\'équipe ?',
        message: '« {name} » ne sera plus proposée à la planification. Le travail qui la nomme déjà continue de la nommer.',
      },
      search: { subtitle: 'Équipe' },
    },
    modules: { teams: 'Équipes' },
    permissions: {
      teams: {
        read: 'Voir les équipes et savoir qui est dans chacune',
        manage: 'Créer des équipes et déplacer des personnes entre elles',
      },
    },
    audit: {
      actions: {
        team: {
          created: 'Équipe créée',
          updated: 'Équipe modifiée',
          member_added: 'Quelqu\'un rejoint une équipe',
          member_removed: 'Quelqu\'un quitte une équipe',
          archived: 'Équipe archivée',
          restored: 'Équipe restaurée',
        },
      },
    },
    assistant: {
      openers: {
        teams: {
          'who-is-with-me': 'Avec qui je travaille aujourd\'hui ?',
          'what-teams': 'Quelles équipes avons-nous ?',
        },
      },
    },
    settings: {
      ns: {
        teams: {
          title: 'Équipes',
          subtitle: 'À quel point cette entreprise est stricte avec ses équipes',
          requireLead: {
            label: 'Exiger un responsable dans chaque équipe',
            help: 'Désactivé par défaut : un binôme qui répond directement au bureau n\'en a pas, et refuser de l\'enregistrer reviendrait à inventer une hiérarchie.',
          },
          oneTeamPerPerson: {
            label: 'Une personne ne peut être que dans une seule équipe',
            help: 'Activez-le quand les équipes sont fixes et que « où est Jean aujourd\'hui » ne doit avoir qu\'une réponse.',
          },
        },
      },
    },
  },
}

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

Object.assign(messages, withToolLabels(messages))
Object.assign(messages, withErrorReasons(messages))

export default { routes, navigation, messages }
